import { alpha, type Theme } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import {
  ALPHA_HOVER,
  INNER_RADIUS,
  overlayShadow,
  tintChipSx,
} from "@/features/reassistRequests/utils/tokens";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";

/** 地図の上に重ねる部品の影（表・ダイアログと同じ）。 */
const cardShadow = overlayShadow;

/** 未購入・一部購入の Chip（0.12 の薄地に dark の文字）。 */
export const saleTintChipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  ...tintChipSx(theme),
});

/** 購入済みの Chip。success は購入済みだけに使う。 */
export const successChipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontWeight: 600,
  color: theme.palette.success.dark,
  backgroundColor: alpha(theme.palette.success.main, 0.12),
});

/** 資料なしの Chip。 */
export const neutralChipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.bluegray.main,
});

/** 属性（建物の種類など）の Chip。状態ではないので色を付けず、白地と border.main の1px の枠にする。 */
export const attributeChipSx = (theme: Theme): SystemStyleObject<Theme> => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.white.main,
  border: `1px solid ${theme.palette.border.main}`,
});

/** 地図のピン（と凡例の見本）の描画値。未購入は輪郭が主役、購入済みは中実で白縁。 */
export interface SalePinStyle {
  color: string;
  weight: number;
  fillColor: string;
  fillOpacity: number;
  radius: number;
}

/** ピンの半径（px）。検索結果のピンと並んでも見えるよう、以前（9）の約1.6倍にする。 */
export const SALE_PIN_RADIUS = 14;

export const salePinStyle = (theme: Theme, purchased: boolean): SalePinStyle =>
  purchased
    ? {
        // 購入済みは、サービスの基調色（ティール）の塗りに白い縁にする（未購入のオレンジと見分けやすい）
        color: theme.palette.white.main,
        weight: 3,
        fillColor: theme.palette.primary.main,
        fillOpacity: 0.9,
        radius: SALE_PIN_RADIUS,
      }
    : {
        // 未購入（これから買える）は、地図の他の色に埋もれないよう、オレンジの塗りに白い縁にする
        color: theme.palette.white.main,
        weight: 3,
        fillColor: theme.palette.warning.main,
        fillOpacity: 0.9,
        radius: SALE_PIN_RADIUS,
      };

/** ピンの当たり判定の半径（px）。SP は直径 44。見た目の円（半径 14 + 線）より小さくしない。 */
export const SALE_PIN_HIT_RADIUS = { sp: 22, pc: 18 } as const;

/** このズーム以上で、ピンのそばに物件名のラベルを常に出す。 */
export const SALE_PIN_LABEL_MIN_ZOOM = 16;

/**
 * 役所資料のピンを描く Leaflet の pane。検索結果のピン（markerPane, z-index 600）の下に隠れないよう、
 * その上・Tooltip（650）とポップアップ（700）の下に置く。
 */
export const SALE_PIN_PANE = {
  name: "reassistSalePinsPane",
  zIndex: 620,
} as const;

/** ピンのそばに常に出す物件名のラベル（Leaflet の Tooltip）の class 名。 */
export const SALE_PIN_LABEL_CLASS = "reassist-sale-pin-label";

/** 地図の高さ。dvh 未対応向けに vh を併記する。 */
export const saleMapHeight = {
  xs: "clamp(360px, 60vh, 560px)",
  [MOBILE_BREAKPOINT]: 560,
};

/** 地図に重ねる白地の入れ物（凡例・案内バー）。 */
export const mapOverlaySx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: "absolute",
  zIndex: 1000,
  boxSizing: "border-box",
  px: 1.5,
  py: 1,
  backgroundColor: theme.palette.white.main,
  borderRadius: INNER_RADIUS,
  boxShadow: cardShadow(theme),
});

/** 案内バー truncated 用の薄地（白の上に重ね、地図を透かさない）。 */
export const mapNoticeTintSx = (theme: Theme): SystemStyleObject<Theme> => {
  const tint = alpha(theme.palette.primary.main, ALPHA_HOVER);
  return { backgroundImage: `linear-gradient(${tint}, ${tint})` };
};

/** フォーカスリング（overflow hidden の入れ物の内側に入れる）。 */
export const focusRingInsetSx = (theme: Theme): SystemStyleObject<Theme> => ({
  "&:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "-2px",
  },
});

/** reduce-motion のとき transition を止める。 */
export const NO_MOTION = {
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
} as const;

/** 地図の入れ物に当てる Leaflet の見た目の上書き。 */
export const saleMapThemeSx = (theme: Theme): SystemStyleObject<Theme> => ({
  "& .leaflet-container:focus-visible": {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: "-2px",
  },
  "& .leaflet-touch .leaflet-bar a": {
    width: MIN_TAP_TARGET,
    height: MIN_TAP_TARGET,
    lineHeight: `${MIN_TAP_TARGET}px`,
  },
  "& .leaflet-tooltip": {
    fontFamily: "inherit",
    fontSize: 13,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.white.main,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: INNER_RADIUS,
    boxShadow: cardShadow(theme),
    whiteSpace: "normal",
    width: "max-content",
    maxWidth: theme.spacing(30),
    px: 1,
    py: 0.5,
  },
  "& .leaflet-tooltip-top:before": {
    borderTopColor: theme.palette.white.main,
  },
  "& .leaflet-tooltip-bottom:before": {
    borderBottomColor: theme.palette.white.main,
  },
  "& .leaflet-tooltip-left:before": {
    borderLeftColor: theme.palette.white.main,
  },
  "& .leaflet-tooltip-right:before": {
    borderRightColor: theme.palette.white.main,
  },
  "@media (prefers-reduced-motion: reduce)": {
    "& .leaflet-fade-anim .leaflet-tile, & .leaflet-zoom-anim .leaflet-zoom-animated":
      { transition: "none" },
  },
});
